import { baseApi } from 'api'
import { ApiResponse } from 'helpers/types'
import {
  ContactChangeRequest,
  ContactItemResponse,
  ContactListResponse,
  CustomerItem,
  CustomerListResponse
} from 'schema'

import { TContact } from '../types'

export const contactApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getContactList: build.query<TContact[], string>({
      query: (search) => ({
        url: 'contact',
        params: search
          ? {
              'filter[search]': search
            }
          : undefined,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ContactListResponse>) => response.data.contact
    }),
    getContactById: build.query<TContact, string>({
      query: (id) => ({
        url: `contact/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ContactItemResponse>) => response.data.contact
    }),
    deleteContact: build.mutation<string, string>({
      query: (id) => ({
        url: `contact/${id}`,
        method: 'DELETE'
      })
    }),
    createContact: build.mutation<ApiResponse<ContactItemResponse>, ContactChangeRequest>({
      query: ({ lastname, firstname, secondname, phone, phoneExt }) => {
        const defaultRequestBody = {
          lastname,
          firstname,
          phone,
          phoneExt
        }
        const requestBody = secondname?.trim() ? { ...defaultRequestBody, secondname } : defaultRequestBody

        return {
          url: 'contact',
          method: 'POST',
          body: requestBody
        }
      }
    }),
    updateContact: build.mutation<ApiResponse<ContactItemResponse>, ContactChangeRequest & { id: TContact['id'] }>({
      query: ({ id, lastname, firstname, secondname, phone, phoneExt }) => {
        const defaultRequestBody = {
          id,
          lastname,
          firstname,
          phone,
          phoneExt
        }
        const requestBody = secondname?.trim() ? { ...defaultRequestBody, secondname } : defaultRequestBody

        return {
          url: `contact/${id}`,
          method: 'PUT',
          body: requestBody
        }
      }
    }),
    getCustomersByContactId: build.query<CustomerItem[], string>({
      query: (id) => ({
        url: `contact/${id}/customer`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<CustomerListResponse>) => response.data.customer
    }),
    attachCustomerByContactId: build.mutation<string, { contactId: CustomerItem['id']; id: TContact['id'] }>({
      query: ({ contactId, id }) => ({
        url: `contact/${contactId}/customer/${id}`,
        method: 'POST'
      })
    }),
    detachCustomerByContactId: build.mutation<string, { contactId: CustomerItem['id']; id: TContact['id'] }>({
      query: ({ contactId, id }) => ({
        url: `contact/${contactId}/customer/${id}`,
        method: 'DELETE'
      })
    })
  }),
  overrideExisting: false
})

export const prepareContactUpdateData = (contact: TContact) => ({
  ...contact,
  secondname: contact.secondname || undefined,
  phoneExt: contact.phoneExt || undefined
})

export const {
  useGetContactListQuery,
  useLazyGetContactListQuery,
  useLazyGetContactByIdQuery,
  useDeleteContactMutation,
  useCreateContactMutation,
  useUpdateContactMutation,
  useGetCustomersByContactIdQuery,
  useAttachCustomerByContactIdMutation,
  useDetachCustomerByContactIdMutation
} = contactApi
